import { FC } from "react";
import { Divider, Form, Input } from "antd";
import { useTranslation } from "react-i18next";
import { AirplaneIcon } from "@Icons/index";
import PulseLoadingIndicator from "@Components/PulseLoadingIndicator/PulseLoadingIndicator";
import { POPULAR_DESTINATIONS } from "../constants";
import NoData from "@Components/NoData/NoData";

const FlightSelectOptions: FC<any> = ({ options = [], onChange, hasError, hasSearch, isFetching }) => {
  const { t } = useTranslation();
  const showPopular = isFetching || !hasSearch || (options.length === 0 && !hasError);
  const displayTitle = showPopular ? t("Flights.search.popularLocations") : t("Flights.search.searchResults");
  const displayOptions = showPopular ? POPULAR_DESTINATIONS : options;

  if (!isFetching && hasError && hasSearch)
    return (
      <div className="flex h-full w-full items-center justify-center p-5">
        <NoData description={t("Flights.search.noAirportsFound", { query: hasSearch })} />
      </div>
    );

  return (
    <div className="flex w-full flex-col gap-1 px-2">
      {displayTitle && <div className="font-semibold text-font-subtle">{displayTitle}</div>}
      <div className="divide-border-subtle flex flex-col divide-y">
        {displayOptions.map((option: any) => {
          const subLabel = option?.city !== "" ? `${option.city}, ` : "";
          return (
            <div
              key={option.value}
              className="w-full! flex items-center gap-1 py-2"
              onClick={() => onChange?.({ label: option.label, value: option.value })}
            >
              <div className="text-font-active flex h-1 w-1 flex-1 items-center justify-center font-bold">
                <AirplaneIcon strokeWidth={1.2} transform="rotate(-45 0 0) scale(.8,.8)" />
              </div>
              <div className="flex-9 flex flex-col items-start justify-center">
                <span className="whitespace-break-spaces font-semibold text-font-default">
                  {option.airport} ({option.iataCode})
                </span>
                <span className="text-font-subtle">
                  {subLabel} {option.country}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FlightSelectMob: FC<any> = ({
  isFetching,
  onSearch,
  onChange,
  options = [],
  icon: Icon,
  error,
  searchValue,
  validationError,
}) => {
  const hasValidationErrors = validationError.length >= 1;

  return (
    <div className="flex flex-col gap-2">
      <div className="shadow-border-subtle sticky top-0 z-10 w-full bg-background-default p-4 shadow-sm">
        <div className="gap-2! outline-border-default focus-within:outline-background-primary-lighter flex shrink-0 items-center justify-center rounded-sm! px-4 outline">
          {Icon && (
            <div className="text-icon-subtle">
              <Icon className="h-5 w-5 text-current" />
            </div>
          )}
          <Input
            autoFocus={true}
            value={searchValue}
            suffix={<PulseLoadingIndicator show={isFetching} running={isFetching} className="bg-background-primary-lighter" />}
            onChange={(e) => {
              onSearch?.(e.target.value);
            }}
            variant="borderless"
            className="p-4! font-semibold!"
          />
        </div>
      </div>
      <div className="px-3">{hasValidationErrors && <div className="flex items-center justify-start text-font-danger">{validationError?.[0]}</div>}</div>

      <div className="flex w-full flex-col px-3 py-1">
        <FlightSelectOptions options={options} onChange={onChange} hasError={error} hasSearch={searchValue} isFetching={isFetching} />
      </div>
    </div>
  );
};

export default FlightSelectMob;
